import { beautifyNumber } from '../../../Utils/FormatNumber';
import { buildProfitAndLossExcelFromDocument } from './editableProfitAndLossExcelExport';
import { refreshPartnersEquitySectionInDocument } from '../../../Utils/partners/partnersEquityDistribution';

export const SELECTED_CLASS = 'pl-editable-selected';
export const EDITED_CLASS = 'pl-editable-edited';
const INLINE_INPUT_CLASS = 'pl-editable-cell-input';
const ACCOUNT_LABEL_CLASS = 'pl-editable-account-label';
const ROW_ACTIONS_CLASS = 'pl-editable-row-actions';
const ACCOUNT_CELL_INNER_CLASS = 'pl-editable-account-cell-inner';
export const ADDED_ROW_CLASS = 'pl-editable-added-row';
export const ADDED_ROW_DEFAULT_NAME = 'שורה חדשה';
export const EMPTY_SECTION_ADD_ROW_CLASS = 'pl-editable-empty-add-row';
const EMPTY_SECTION_ADD_CELL_CLASS = 'pl-editable-empty-add-cell';
const EMPTY_SECTION_ADD_BTN_CLASS = 'pl-editable-empty-add-btn';
const EMPTY_SECTION_DEFAULT_ADD_LABEL = 'הוסף שורה';

/** Subtotal / total rows that always sit after the data rows inside a section table. */
const SECTION_TOTAL_ROW_SELECTOR = [
  'tr.tr-final-row-palcode',
  'tr.tr-final-row-palcode-category',
  'tr.pal-code-account-group-total-row',
  'tr.ag-classification-total-row',
  'tr.tr-final-row-palcode-Report',
  'tr.pl-profit-indicator-row',
].join(', ');

/**
 * Parse user input or displayed amount text into a number.
 * Supports: 1,215,151.00 | -3869.80 | (3,869.80)
 */
export const parseEditableAmount = (raw) => {
  if (raw === null || raw === undefined) return NaN;
  const trimmed = String(raw).trim();
  if (!trimmed) return NaN;

  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
    const inner = trimmed.slice(1, -1).replace(/,/g, '').replace(/\s+/g, '');
    const num = parseFloat(inner);
    return isNaN(num) ? NaN : -Math.abs(num);
  }

  const normalized = trimmed.replace(/,/g, '').replace(/\s+/g, '');
  const num = parseFloat(normalized);
  return isNaN(num) ? NaN : num;
};

export const formatEditableAmount = (num) => beautifyNumber(num);

export const formatPercentage = (value, totalIncome) => {
  if (!totalIncome || totalIncome <= 0) return '0.00%';
  return `${((value / totalIncome) * 100).toFixed(2)}%`;
};

export const getExportHtmlFromIframe = (iframeEl, { columnMode } = {}) => {
  if (!iframeEl?.contentDocument?.documentElement) return '';

  if (columnMode !== 'totals') {
    return `<!DOCTYPE html>\n${iframeEl.contentDocument.documentElement.outerHTML}`;
  }

  const clone = iframeEl.contentDocument.documentElement.cloneNode(true);
  stripMonthlyColumnsFromRoot(clone);
  convertLandscapePageToPortrait(clone);
  return `<!DOCTYPE html>\n${clone.outerHTML}`;
};

const isTotalHeaderText = (text) => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value) return false;
  return /סה["״]?כ/.test(value) || /^total$/i.test(value);
};

const getDirectRowCells = (tr) =>
  [...tr.children].filter((el) => el.tagName === 'TH' || el.tagName === 'TD');

const expandRowCells = (tr) => {
  const items = [];
  let col = 0;
  getDirectRowCells(tr).forEach((cell) => {
    const span = Math.max(parseInt(cell.getAttribute('colspan') || '1', 10), 1);
    items.push({ cell, start: col, end: col + span });
    col += span;
  });
  return { items, columnCount: col };
};

const findPeriodHeaderRow = (table) =>
  [...table.querySelectorAll('thead tr')].find((tr) => tr.querySelector('th.td-header-period'));

export const getHtmlKeptColumnIndexes = (table) => {
  const periodRow = findPeriodHeaderRow(table);
  if (!periodRow) return null;

  const { items, columnCount } = expandRowCells(periodRow);
  if (columnCount <= 1) return null;

  const kept = new Set([0]);
  let firstTotalIdx = -1;
  items.forEach(({ cell, start, end }) => {
    if (start === 0) return;
    if (!isTotalHeaderText(cell.textContent)) return;
    if (firstTotalIdx < 0) firstTotalIdx = start;
    for (let i = start; i < end; i += 1) kept.add(i);
  });

  if (firstTotalIdx < 0) return null;

  for (let i = firstTotalIdx; i < columnCount; i += 1) kept.add(i);
  const indexes = [...kept].sort((a, b) => a - b);
  if (indexes.length >= columnCount) return null;
  return indexes;
};

const filterRowToColumns = (tr, keptIndexes) => {
  const keptSet = new Set(keptIndexes);
  const { items } = expandRowCells(tr);
  items.forEach(({ cell, start, end }) => {
    let overlap = 0;
    for (let i = start; i < end; i += 1) {
      if (keptSet.has(i)) overlap += 1;
    }
    if (overlap === 0) {
      cell.remove();
      return;
    }
    const span = end - start;
    if (overlap === span) return;
    if (overlap <= 1) cell.removeAttribute('colspan');
    else cell.setAttribute('colspan', String(overlap));
  });
};

const replaceColGroup = (table, columnCount) => {
  const doc = table.ownerDocument;
  if (!doc) return;

  const numeric = Math.max(columnCount - 1, 0);
  const accountPct = numeric <= 1 ? 70 : numeric === 2 ? 54 : 26;
  const numericPct = numeric > 0 ? (100 - accountPct) / numeric : 0;

  let colgroup = table.querySelector('colgroup');
  if (!colgroup) {
    colgroup = doc.createElement('colgroup');
    table.insertBefore(colgroup, table.firstChild);
  }
  colgroup.innerHTML = '';

  const accountCol = doc.createElement('col');
  accountCol.className = 'col-account';
  accountCol.style.width = `${accountPct}%`;
  colgroup.appendChild(accountCol);

  for (let i = 0; i < numeric; i += 1) {
    const col = doc.createElement('col');
    col.className = 'col-numeric';
    col.style.width = `${numericPct}%`;
    colgroup.appendChild(col);
  }
};

const LAYOUT_CLASSES = [
  'pl-cols-compact',
  'pl-cols-dual',
  'pl-cols-medium',
  'pl-cols-dense',
  'pl-cols-extra',
];

const updateTableLayoutClass = (table, columnCount) => {
  const numeric = Math.max(columnCount - 1, 0);
  LAYOUT_CLASSES.forEach((cls) => table.classList.remove(cls));
  if (numeric <= 1) table.classList.add('pl-cols-compact');
  else if (numeric === 2) table.classList.add('pl-cols-dual');
};

const stripTableToTotalColumns = (table) => {
  const keptIndexes = getHtmlKeptColumnIndexes(table);
  if (!keptIndexes?.length) return;

  [...table.querySelectorAll('tr')].forEach((tr) => filterRowToColumns(tr, keptIndexes));
  replaceColGroup(table, keptIndexes.length);
  updateTableLayoutClass(table, keptIndexes.length);
};

export const stripMonthlyColumnsFromRoot = (root) => {
  if (!root?.querySelectorAll) return root;
  root.querySelectorAll('table').forEach((table) => {
    if (table.closest('.pl-year-summary')) return;
    stripTableToTotalColumns(table);
  });
  return root;
};

const convertLandscapePageToPortrait = (root) => {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll('style').forEach((styleEl) => {
    const css = styleEl.textContent;
    if (!css?.includes('landscape')) return;
    styleEl.textContent = css.replace(
      /size:\s*A4 landscape;\s*margin:\s*7mm 8mm 10mm 8mm;/g,
      'size: A4; margin: 8mm 10mm 16mm 10mm;',
    );
  });
};

export const cleanProfitAndLossHtmlForExport = (html) => {
  if (!html) return '';
  return html
    .replace(/border: 1px solid #ccc;/g, '')
    .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
    .replace(/\s*class="hoverTr"/g, '')
    .replace(/\s*hoverTr\s*/g, '')
    .replace(/<style id="pl-preview-iframe-styles">[\s\S]*?<\/style>/g, '')
    .replace(/<style id="pl-editable-iframe-styles">[\s\S]*?<\/style>/g, '')
    .replace(/\s*pl-editable-selected/g, '')
    .replace(/\s*pl-editable-edited/g, '')
    .replace(/\s*pl-editable-data-row/g, '')
    .replace(/\s*pl-editable-added-row/g, '')
    .replace(/\s*data-editable-cell="[^"]*"/g, '')
    .replace(/\s*data-editable-account-name="[^"]*"/g, '')
    .replace(/\s*data-cell-id="[^"]*"/g, '')
    .replace(/\s*data-original-text="[^"]*"/g, '')
    .replace(/\s*data-original-account-name="[^"]*"/g, '')
    .replace(/\s*data-original-row-key="[^"]*"/g, '')
    .replace(/\s*data-local-row-id="[^"]*"/g, '')
    .replace(/\s*data-added-row="[^"]*"/g, '')
    .replace(/\s*data-account-group="[^"]*"/g, '')
    .replace(/\s*data-pal-code="[^"]*"/g, '')
    .replace(/\s*data-inline-original="[^"]*"/g, '')
    .replace(/<tr[^>]*class="[^"]*pl-editable-empty-add-row[^"]*"[\s\S]*?<\/tr>/g, '')
    .replace(/<span class="pl-editable-row-actions"[\s\S]*?<\/span>/g, '')
    .replace(
      /<div class="pl-editable-account-cell-inner">\s*<span class="pl-editable-account-label">([\s\S]*?)<\/span>[\s\S]*?<\/div>/g,
      '$1',
    )
    .replace(/<input[^>]*class="[^"]*pl-editable-cell-input[^"]*"[^>]*\/?>/g, '')
    .replace(/\s*onclick="[^"]*"/g, '');
};

export const getAmountFromCell = (cell) => {
  if (!cell) return 0;
  const input = cell.querySelector(`.${INLINE_INPUT_CLASS}`);
  const text = input
    ? input.value
    : (cell.querySelector('.accounting-amount-value')?.textContent ?? cell.textContent);
  const parsed = parseEditableAmount(text);
  return isNaN(parsed) ? 0 : parsed;
};

export const setAmountCellDisplay = (cell, num) => {
  if (!cell) return;
  const formatted = formatEditableAmount(num).trim();
  const valueSpan = cell.querySelector('.accounting-amount-value');
  if (valueSpan) {
    valueSpan.textContent = formatted;
    return;
  }
  const hasUnderlineWrapper = cell.querySelector(
    '.accounting-amount-underline-double, .accounting-amount-underline-triple, .accounting-amount-underline-dotted'
  );
  if (!hasUnderlineWrapper) {
    cell.textContent = formatted;
  }
};

export const setPercentageDisplay = (cell, value, totalIncome) => {
  if (!cell) return;
  const pct = formatPercentage(value, totalIncome);
  const valueSpan = cell.querySelector('.accounting-amount-value');
  if (valueSpan) {
    valueSpan.textContent = pct;
    return;
  }
  const hasUnderlineWrapper = cell.querySelector(
    '.accounting-amount-underline-double, .accounting-amount-underline-triple, .accounting-amount-underline-dotted'
  );
  if (!hasUnderlineWrapper) {
    cell.textContent = pct;
  }
};

export const EDITABLE_SOURCE_LEDGER = 'ledger';
export const EDITABLE_SOURCE_PL_REPORT = 'profitAndLossReport';

const isLedgerEditableRow = (row) => row?.classList?.contains('hoverTr');

const isProfitAndLossReportEditableRow = (row) => {
  if (!row?.classList?.contains('tr')) return false;
  if (row.classList.contains('pl-profit-indicator-row')) return false;
  if (row.querySelector('td.total-palcode')) return false;
  return Boolean(row.querySelector('td.td-account-cell'));
};

export const isEditableDataRow = (row, source = EDITABLE_SOURCE_LEDGER) => {
  if (!row) return false;
  if (source === EDITABLE_SOURCE_PL_REPORT) return isProfitAndLossReportEditableRow(row);
  return isLedgerEditableRow(row);
};

export const getEditableRows = (doc, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc) return [];
  if (source === EDITABLE_SOURCE_PL_REPORT) {
    return [...doc.querySelectorAll('tr.tr')].filter(isProfitAndLossReportEditableRow);
  }
  return [...doc.querySelectorAll('tr.hoverTr')];
};

export const getEditableAmountCell = (row, doc = null, source = EDITABLE_SOURCE_LEDGER) => {
  const cells = getEditableAmountCells(row, doc, source);
  return cells[0] || null;
};

export const getPeriodColumnCount = (doc) => {
  if (!doc) return 1;
  const periodHeaders = doc.querySelectorAll('thead th.td-header-period');
  if (periodHeaders.length > 0) return periodHeaders.length;
  return 1;
};

const rowHasTotalAmountColumn = (row, periodCount) => {
  if (!row || periodCount <= 1) return false;
  return row.querySelectorAll('td.td-amount-cell').length > periodCount;
};

/** Period amount cells only — excludes the computed row-total column when present. */
export const getEditableAmountCells = (row, doc = null, source = EDITABLE_SOURCE_LEDGER) => {
  if (!row) return [];

  if (source !== EDITABLE_SOURCE_PL_REPORT) {
    const cell = row.querySelector('td.td-amount-cell');
    return cell ? [cell] : [];
  }

  const allAmountCells = [...row.querySelectorAll('td.td-amount-cell')];
  if (!allAmountCells.length) return [];

  const periodCount = getPeriodColumnCount(doc);
  const editableCount = rowHasTotalAmountColumn(row, periodCount)
    ? periodCount
    : Math.min(allAmountCells.length, periodCount);

  return allAmountCells.slice(0, editableCount);
};

export const getEditablePercentageCell = (row) => {
  if (!row) return null;
  return row.querySelector('td.td-percentage');
};

export const getAccountCellLabel = (cell) => {
  if (!cell) return '';
  const input = cell.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (input) return input.value.trim();
  const label = cell.querySelector(`.${ACCOUNT_LABEL_CLASS}`);
  if (label) return label.textContent.trim();
  return (cell.textContent || '').trim();
};

export const setAccountCellLabel = (cell, text) => {
  if (!cell) return;
  ensureAccountCellStructure(cell);
  const label = cell.querySelector(`.${ACCOUNT_LABEL_CLASS}`);
  if (label) {
    label.textContent = text;
    return;
  }
  cell.textContent = text;
};

export const ensureAccountCellStructure = (cell) => {
  if (!cell || cell.querySelector(`.${ACCOUNT_CELL_INNER_CLASS}`)) return;

  const labelText = getAccountCellLabel(cell);
  cell.innerHTML = '';

  const inner = cell.ownerDocument.createElement('div');
  inner.className = ACCOUNT_CELL_INNER_CLASS;

  const label = cell.ownerDocument.createElement('span');
  label.className = ACCOUNT_LABEL_CLASS;
  label.textContent = labelText;

  inner.appendChild(label);
  cell.appendChild(inner);
};

const createRowActionButtons = (doc, { onAddRow, onDeleteRow }) => {
  const actions = doc.createElement('span');
  actions.className = ROW_ACTIONS_CLASS;

  const addBtn = doc.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'pl-editable-row-action pl-editable-row-action--add';
  addBtn.title = 'Add row';
  addBtn.textContent = '+';
  addBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    onAddRow?.();
  });

  const deleteBtn = doc.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.className = 'pl-editable-row-action pl-editable-row-action--delete';
  deleteBtn.title = 'Delete row';
  deleteBtn.textContent = '×';
  deleteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    onDeleteRow?.();
  });

  actions.appendChild(addBtn);
  actions.appendChild(deleteBtn);
  return actions;
};

const decorateEditableRow = (row, doc, rowActionHandlers) => {
  row.classList.add('pl-editable-data-row');
  row.style.cursor = 'default';

  const accountCell = row.querySelector('td.td-account-cell');
  if (accountCell) {
    ensureAccountCellStructure(accountCell);
    if (!accountCell.hasAttribute('data-original-account-name')) {
      accountCell.setAttribute('data-original-account-name', getAccountCellLabel(accountCell));
    }
    accountCell.setAttribute('data-editable-account-name', 'true');

    const inner = accountCell.querySelector(`.${ACCOUNT_CELL_INNER_CLASS}`);
    if (inner && !inner.querySelector(`.${ROW_ACTIONS_CLASS}`)) {
      inner.appendChild(createRowActionButtons(doc, {
        onAddRow: () => rowActionHandlers.onAddRow?.(row),
        onDeleteRow: () => rowActionHandlers.onDeleteRow?.(row),
      }));
    }
  }
};

const getRowClassName = (source) => (source === EDITABLE_SOURCE_PL_REPORT ? 'tr' : 'hoverTr');

export const buildAddedRowElement = (doc, referenceRow, descriptor, source = EDITABLE_SOURCE_LEDGER) => {
  const tr = doc.createElement('tr');
  tr.className = `${getRowClassName(source)} ${ADDED_ROW_CLASS}`.trim();
  tr.setAttribute('data-added-row', 'true');
  tr.setAttribute('data-local-row-id', descriptor.localRowId);
  tr.setAttribute('data-original-row-key', `added:${descriptor.localRowId}`);
  if (descriptor.accountGroup) tr.setAttribute('data-account-group', descriptor.accountGroup);
  if (descriptor.palCode) tr.setAttribute('data-pal-code', descriptor.palCode);
  if (descriptor.sortCodeName) tr.setAttribute('data-sort-code-name', descriptor.sortCodeName);

  const refCells = [...referenceRow.querySelectorAll(':scope > td')];
  let amountCellIndex = 0;

  refCells.forEach((refCell) => {
    const td = doc.createElement('td');

    if (refCell.classList.contains('td-account-cell')) {
      td.className = 'td-account-cell';
      td.textContent = descriptor.accountName || ADDED_ROW_DEFAULT_NAME;
    } else if (refCell.classList.contains('td-amount-cell')) {
      td.className = 'td-amount-cell';
      const amount = descriptor.amounts?.[amountCellIndex] ?? 0;
      td.textContent = formatEditableAmount(amount).trim();
      amountCellIndex += 1;
    } else if (refCell.classList.contains('td-percentage')) {
      td.className = 'td-percentage';
      td.textContent = '0.00%';
    } else {
      td.className = refCell.className;
      td.textContent = refCell.textContent;
    }

    tr.appendChild(td);
  });

  return tr;
};

const findRowByKey = (doc, rowKey, source) => {
  if (!rowKey || !doc) return null;
  if (rowKey.startsWith('added:')) {
    const localRowId = rowKey.slice('added:'.length);
    return doc.querySelector(`[data-local-row-id="${localRowId}"]`);
  }
  return getEditableRows(doc, source).find((row) => makeEditKeyFromRow(row, source) === rowKey) || null;
};

const findInsertAnchorRow = (doc, descriptor, source) => {
  if (descriptor.afterLocalRowId) {
    const addedAnchor = doc.querySelector(`[data-local-row-id="${descriptor.afterLocalRowId}"]`);
    if (addedAnchor) return addedAnchor;
  }
  if (descriptor.afterRowKey) {
    const anchor = findRowByKey(doc, descriptor.afterRowKey, source);
    if (anchor) return anchor;
  }
  return null;
};

/** All section tables that can contain editable data rows, in document order. */
const getSectionTables = (doc) => [...(doc?.querySelectorAll('table.table') || [])];

const tableHasEditableDataRow = (table, source) =>
  [...(table?.querySelector('tbody')?.children || [])].some((tr) =>
    isEditableDataRow(tr, source),
  );

/** Stable-within-session key for an (otherwise empty) section table. */
const getSectionTableKey = (doc, table) => {
  const index = getSectionTables(doc).indexOf(table);
  return index === -1 ? null : index;
};

const resolveSectionTableByKey = (doc, key) => {
  if (key == null) return null;
  return getSectionTables(doc)[Number(key)] || null;
};

const getAccountGroupForTable = (table) => {
  const page = table?.closest('.pageStyle');
  if (!page) return '';
  const detailLabel = page.querySelector('.section-header-detail-label');
  const agLabel = page.querySelector('.section-header-ag-label');
  const text = (detailLabel?.textContent || agLabel?.textContent || '').trim();
  if (!text) return '';
  return text.split(/\s*-\s*/)[0]?.trim() || text;
};

const getPalCodeForTable = (table) => {
  const overflow = table?.querySelector('tbody td.overflowed');
  return overflow ? overflow.textContent.trim() : '';
};

/**
 * Build a detached reference row matching the section's column layout (account
 * cell, amount cell(s) and an optional percentage cell) so a brand-new row can be
 * created for a section that currently has no data rows to clone from.
 */
const buildSyntheticReferenceRow = (table, doc) => {
  const tr = doc.createElement('tr');
  const cols = [...(table?.querySelectorAll('colgroup col') || [])];
  const headerThs = [...(table?.querySelectorAll('thead th') || [])];
  const hasPercentage = headerThs.some((th) => (th.textContent || '').includes('%'));

  let numericCount;
  if (cols.length) numericCount = Math.max(cols.length - 1, 1);
  else numericCount = Math.max(headerThs.length - 1, 1);

  const accountTd = doc.createElement('td');
  accountTd.className = 'td-account-cell';
  tr.appendChild(accountTd);

  for (let i = 0; i < numericCount; i += 1) {
    const td = doc.createElement('td');
    // Single-period layout: amount column(s) first, then the percentage column.
    td.className = hasPercentage && i === numericCount - 1 ? 'td-percentage' : 'td-amount-cell';
    tr.appendChild(td);
  }

  return tr;
};

/** Place a freshly created data row before the section subtotals (after any pal-code header). */
const insertDataRowIntoSection = (tbody, newRow) => {
  if (!tbody) return;

  const firstTotal = tbody.querySelector(SECTION_TOTAL_ROW_SELECTOR);
  if (firstTotal) {
    tbody.insertBefore(newRow, firstTotal);
    return;
  }

  const overflowRows = [...tbody.children].filter((tr) => tr.querySelector('td.overflowed'));
  const lastOverflow = overflowRows[overflowRows.length - 1];
  if (lastOverflow) {
    lastOverflow.insertAdjacentElement('afterend', newRow);
    return;
  }

  tbody.appendChild(newRow);
};

const getSectionColumnCount = (table) => {
  const cols = table?.querySelectorAll('colgroup col');
  if (cols?.length) return cols.length;
  const headerThs = table?.querySelectorAll('thead th');
  if (headerThs?.length) return headerThs.length;
  return 2;
};

export const applyRowStructureEdits = (
  doc,
  { deletedRows = new Set(), addedRows = [] } = {},
  source = EDITABLE_SOURCE_LEDGER,
  rowActionHandlers = {},
) => {
  if (!doc) return;

  if (deletedRows?.size) {
    getEditableRows(doc, source).forEach((row) => {
      const rowKey = getStableRowKey(row, source);
      if (rowKey && deletedRows.has(rowKey)) {
        row.remove();
      }
    });
  }

  addedRows.forEach((descriptor) => {
    if (doc.querySelector(`[data-local-row-id="${descriptor.localRowId}"]`)) return;

    const anchor = findInsertAnchorRow(doc, descriptor, source);
    if (anchor) {
      const newRow = buildAddedRowElement(doc, anchor, descriptor, source);
      anchor.insertAdjacentElement('afterend', newRow);
      decorateEditableRow(newRow, doc, rowActionHandlers);
      return;
    }

    // First row added to a section that had no rows to anchor to.
    if (descriptor.emptySectionKey != null) {
      const table = resolveSectionTableByKey(doc, descriptor.emptySectionKey);
      const tbody = table?.querySelector('tbody');
      if (!tbody) return;
      const refRow = buildSyntheticReferenceRow(table, doc);
      const newRow = buildAddedRowElement(doc, refRow, descriptor, source);
      insertDataRowIntoSection(tbody, newRow);
      decorateEditableRow(newRow, doc, rowActionHandlers);
    }
  });
};

export const applyRenamesToDocument = (doc, renamesMap, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc || !renamesMap?.size) return;

  getEditableRows(doc, source).forEach((row) => {
    const rowKey = getStableRowKey(row, source);
    if (!rowKey || !renamesMap.has(rowKey)) return;

    const accountCell = row.querySelector('td.td-account-cell');
    if (!accountCell) return;

    setAccountCellLabel(accountCell, renamesMap.get(rowKey));
    accountCell.classList.add(EDITED_CLASS);
  });
};

const getAccountGroupFromRow = (row) => {
  const page = row.closest('.pageStyle');
  if (!page) return '';
  const detailLabel = page.querySelector('.section-header-detail-label');
  const agLabel = page.querySelector('.section-header-ag-label');
  const text = (detailLabel?.textContent || agLabel?.textContent || '').trim();
  if (!text) return '';
  const parts = text.split(/\s*-\s*/);
  return parts[0]?.trim() || text;
};

const getPalCodeFromRow = (row) => {
  let el = row.previousElementSibling;
  while (el) {
    if (el.matches?.('tr')) {
      const overflowed = el.querySelector('td.overflowed');
      if (overflowed) {
        return overflowed.textContent.trim();
      }
    }
    el = el.previousElementSibling;
  }
  return '';
};

/** Parse `סה''כ {groupName}:` from pal-code-account-group total rows */
const extractGroupNameFromTotalRow = (tr) => {
  const text = tr.querySelector('.total-palcode')?.textContent?.trim() || '';
  if (!text.includes('סה')) return '';
  return text.replace(/^סה[״"']?כ\s*/u, '').replace(/:+\s*$/, '').trim();
};

const sumAmountsForAccountGroup = (doc, groupName, source = EDITABLE_SOURCE_LEDGER) => {
  if (!groupName) return 0;
  let sum = 0;
  getEditableRows(doc, source).forEach((row) => {
    const group = row.getAttribute('data-account-group') || getAccountGroupFromRow(row);
    if (group === groupName) {
      getEditableAmountCells(row, doc, source).forEach((cell) => {
        sum += getAmountFromCell(cell);
      });
    }
  });
  return sum;
};

const updateProfitIndicatorRowAmounts = (
  tr,
  valuesByPeriod,
  periodCount,
  hasRowTotalColumn,
  incomeGrandTotal,
) => {
  const amountCells = [...tr.querySelectorAll('td.td-last-sum, td.td-amount-cell')];
  for (let i = 0; i < periodCount; i += 1) {
    if (amountCells[i]) setAmountCellDisplay(amountCells[i], valuesByPeriod[i]);
  }
  if (hasRowTotalColumn && amountCells[periodCount]) {
    const total = valuesByPeriod.reduce((sum, value) => sum + value, 0);
    setAmountCellDisplay(amountCells[periodCount], total);
    const pctCell = amountCells[periodCount].nextElementSibling;
    if (pctCell?.classList.contains('td-percentage')) {
      setPercentageDisplay(pctCell, total, incomeGrandTotal);
    }
  }
};

/** Recalculate dotted-underline rows: רווח גולמי / רווח תפעולי (cumulative income minus expenses). */
const recalculatePlProfitIndicatorRows = (doc, source = EDITABLE_SOURCE_PL_REPORT) => {
  const periodCount = getPeriodColumnCount(doc);
  const editableRows = getEditableRows(doc, source);
  const hasRowTotalColumn = rowHasTotalAmountColumn(editableRows[0], periodCount);

  const cumulative = Array(periodCount).fill(0);
  editableRows.forEach((row) => {
    if ((row.getAttribute('data-account-group') || '') !== 'הכנסות') return;
    getEditableAmountCells(row, doc, source).forEach((cell, i) => {
      cumulative[i] += getAmountFromCell(cell);
    });
  });

  const incomeGrandTotal = cumulative.reduce((sum, value) => sum + value, 0);

  doc.querySelectorAll('table.table tbody').forEach((tbody) => {
    [...tbody.children].forEach((tr) => {
      if (isEditableDataRow(tr, source)) {
        const group = tr.getAttribute('data-account-group') || '';
        if (group === 'הכנסות') return;
        getEditableAmountCells(tr, doc, source).forEach((cell, i) => {
          cumulative[i] += getAmountFromCell(cell);
        });
      } else if (tr.classList.contains('pl-profit-indicator-row')) {
        updateProfitIndicatorRowAmounts(
          tr,
          cumulative,
          periodCount,
          hasRowTotalColumn,
          incomeGrandTotal,
        );
      }
    });
  });
};

const recalculateMultiPeriodPlReportTotals = (doc, source = EDITABLE_SOURCE_PL_REPORT) => {
  const periodCount = getPeriodColumnCount(doc);
  if (periodCount <= 1) return;

  const editableRows = getEditableRows(doc, source);
  const hasRowTotalColumn = rowHasTotalAmountColumn(editableRows[0], periodCount);

  const incomeByPeriod = Array(periodCount).fill(0);
  editableRows.forEach((row) => {
    if ((row.getAttribute('data-account-group') || '') !== 'הכנסות') return;
    getEditableAmountCells(row, doc, source).forEach((cell, i) => {
      const amt = getAmountFromCell(cell);
      if (amt > 0) incomeByPeriod[i] += amt;
    });
  });
  const incomeGrandTotal = incomeByPeriod.reduce((sum, value) => sum + value, 0);

  const cumulative = [...incomeByPeriod];

  editableRows.forEach((row) => {
    const periodCells = getEditableAmountCells(row, doc, source);
    const rowSum = periodCells.reduce((sum, cell) => sum + getAmountFromCell(cell), 0);

    if (hasRowTotalColumn) {
      const amountCells = [...row.querySelectorAll('td.td-amount-cell')];
      const totalCell = amountCells[periodCount];
      if (totalCell && !totalCell.querySelector(`.${INLINE_INPUT_CLASS}`)) {
        setAmountCellDisplay(totalCell, rowSum);
      }
      const pctCell = totalCell?.nextElementSibling;
      if (pctCell?.classList.contains('td-percentage')) {
        setPercentageDisplay(pctCell, rowSum, incomeGrandTotal);
      }
    }
  });

  doc.querySelectorAll('table.table tbody').forEach((tbody) => {
    const groupPeriodSums = Array(periodCount).fill(0);
    let categoryPeriodSums = Array(periodCount).fill(0);

    [...tbody.children].forEach((tr) => {
      if (isEditableDataRow(tr, source)) {
        const group = tr.getAttribute('data-account-group') || '';
        getEditableAmountCells(tr, doc, source).forEach((cell, i) => {
          const amt = getAmountFromCell(cell);
          groupPeriodSums[i] += amt;
          categoryPeriodSums[i] += amt;
          if (group !== 'הכנסות') {
            cumulative[i] += amt;
          }
        });
      } else if (tr.classList.contains('pl-profit-indicator-row')) {
        updateProfitIndicatorRowAmounts(
          tr,
          cumulative,
          periodCount,
          hasRowTotalColumn,
          incomeGrandTotal,
        );
      } else if (tr.classList.contains('pal-code-account-group-total-row')) {
        const lastSumCells = [...tr.querySelectorAll('td.td-last-sum')];
        for (let i = 0; i < periodCount; i += 1) {
          if (lastSumCells[i]) setAmountCellDisplay(lastSumCells[i], groupPeriodSums[i]);
        }
        if (hasRowTotalColumn && lastSumCells[periodCount]) {
          const groupTotal = groupPeriodSums.reduce((sum, value) => sum + value, 0);
          setAmountCellDisplay(lastSumCells[periodCount], groupTotal);
          const pctCell = lastSumCells[periodCount].nextElementSibling;
          if (pctCell?.classList.contains('td-percentage')) {
            setPercentageDisplay(pctCell, groupTotal, incomeGrandTotal);
          }
        }
        groupPeriodSums.fill(0);
      } else if (tr.classList.contains('ag-classification-total-row')) {
        const lastCells = [...tr.querySelectorAll('td.td-last')];
        let categoryTotal = 0;
        for (let i = 0; i < periodCount; i += 1) {
          if (lastCells[i]) setAmountCellDisplay(lastCells[i], categoryPeriodSums[i]);
          categoryTotal += categoryPeriodSums[i];
        }
        if (hasRowTotalColumn && lastCells[periodCount]) {
          setAmountCellDisplay(lastCells[periodCount], categoryTotal);
          const pctCell = lastCells[periodCount].nextElementSibling;
          if (pctCell?.classList.contains('td-percentage')) {
            setPercentageDisplay(pctCell, categoryTotal, incomeGrandTotal);
          }
        }
        categoryPeriodSums = Array(periodCount).fill(0);
      }
    });
  });

  const docPeriodSums = Array(periodCount).fill(0);
  editableRows.forEach((row) => {
    getEditableAmountCells(row, doc, source).forEach((cell, i) => {
      docPeriodSums[i] += getAmountFromCell(cell);
    });
  });
  const docGrandTotal = docPeriodSums.reduce((sum, value) => sum + value, 0);

  doc.querySelectorAll('tr.tr-final-row-palcode-Report').forEach((tr) => {
    const amountCells = [...tr.querySelectorAll('td.td-last-sum-1')];
    for (let i = 0; i < periodCount; i += 1) {
      if (amountCells[i]) setAmountCellDisplay(amountCells[i], docPeriodSums[i]);
    }
    if (hasRowTotalColumn && amountCells[periodCount]) {
      setAmountCellDisplay(amountCells[periodCount], docGrandTotal);
      const pctCell = amountCells[periodCount].nextElementSibling;
      if (pctCell?.classList.contains('td-percentage')) {
        setPercentageDisplay(pctCell, docGrandTotal, incomeGrandTotal);
      }
    }
  });
};

export const annotateEditableRows = (doc, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc) return;

  if (source === EDITABLE_SOURCE_PL_REPORT) {
    let currentGroup = '';
    doc.querySelectorAll('tbody tr').forEach((row) => {
      const groupLabel = row.querySelector('.section-header-ag-label');
      if (groupLabel) {
        currentGroup = groupLabel.textContent.trim();
        return;
      }
      if (!isProfitAndLossReportEditableRow(row)) return;
      row.setAttribute('data-account-group', currentGroup);
      const sortCodeName = row.querySelector('.td-account-cell')?.textContent?.trim() || '';
      if (sortCodeName) row.setAttribute('data-sort-code-name', sortCodeName);
      const rowKey = makeEditKeyFromRow(row, source);
      if (rowKey) row.setAttribute('data-original-row-key', rowKey);
    });
    return;
  }

  doc.querySelectorAll('tr.hoverTr').forEach((row) => {
    row.setAttribute('data-account-group', getAccountGroupFromRow(row));
    row.setAttribute('data-pal-code', getPalCodeFromRow(row));
    const rowKey = makeEditKeyFromRow(row, source);
    if (rowKey) row.setAttribute('data-original-row-key', rowKey);
  });
};

/** Sum of every editable period amount in the document — the report's bottom-line total. */
/** A row's month/period amount cells, never the "סה״כ" (row total) column of its own table. */
const getRowPeriodAmountCells = (row, doc, source) => {
  const cells = getEditableAmountCells(row, doc, source);
  if (source !== EDITABLE_SOURCE_PL_REPORT) return cells;

  const headers = [...(row.closest('table')?.querySelectorAll('thead th.td-header-period') || [])];
  const periodHeaderCount = headers.filter((th) => !isTotalHeaderText(th.textContent)).length;
  if (!periodHeaderCount || periodHeaderCount === headers.length) return cells;

  return [...row.querySelectorAll('td.td-amount-cell')].slice(0, periodHeaderCount);
};

export const getDocumentGrandTotal = (doc, source = EDITABLE_SOURCE_LEDGER) =>
  getEditableRows(doc, source).reduce(
    (sum, row) =>
      sum + getRowPeriodAmountCells(row, doc, source).reduce((acc, cell) => acc + getAmountFromCell(cell), 0),
    0,
  );

export const syncPartnersEquityWithDocument = (doc, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc) return;
  refreshPartnersEquitySectionInDocument(doc, getDocumentGrandTotal(doc, source));
};

export const recalculateReportTotals = (doc, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc) return;

  if (source === EDITABLE_SOURCE_PL_REPORT && getPeriodColumnCount(doc) > 1) {
    recalculateMultiPeriodPlReportTotals(doc, source);
    syncPartnersEquityWithDocument(doc, source);
    return;
  }

  const accountRows = getEditableRows(doc, source).map((row) => {
    const amountCell = getEditableAmountCell(row, doc, source);
    const pctCell = getEditablePercentageCell(row);
    return {
      row,
      amountCell,
      pctCell,
      amount: getAmountFromCell(amountCell),
    };
  });

  let totalIncome = 0;
  accountRows.forEach(({ row, amount }) => {
    const group = row.getAttribute('data-account-group') || getAccountGroupFromRow(row);
    if (group === 'הכנסות' && amount > 0) {
      totalIncome += amount;
    }
  });

  accountRows.forEach(({ amountCell, pctCell, amount }) => {
    if (pctCell) {
      setPercentageDisplay(pctCell, amount, totalIncome);
    }
    if (amountCell && !amountCell.querySelector(`.${INLINE_INPUT_CLASS}`)) {
      setAmountCellDisplay(amountCell, amount);
    }
  });

  doc.querySelectorAll('table.table tbody').forEach((tbody) => {
    let palSum = 0;
    let categorySum = 0;

    [...tbody.children].forEach((tr) => {
      if (isEditableDataRow(tr, source)) {
        const amt = getAmountFromCell(getEditableAmountCell(tr, doc, source));
        palSum += amt;
        categorySum += amt;
      } else if (
        tr.classList.contains('tr-final-row-palcode') &&
        !tr.classList.contains('tr-final-row-palcode-category')
      ) {
        const amountTd = tr.querySelector('td.td-last');
        const pctTd = tr.querySelector('td.td-percentage');
        if (amountTd) setAmountCellDisplay(amountTd, palSum);
        if (pctTd) setPercentageDisplay(pctTd, palSum, totalIncome);
        palSum = 0;
      } else if (tr.classList.contains('ag-classification-total-row')) {
        const amountTd = tr.querySelector('td.td-last');
        const pctTd = tr.querySelector('td.td-percentage');
        if (amountTd) setAmountCellDisplay(amountTd, categorySum);
        if (pctTd) setPercentageDisplay(pctTd, categorySum, totalIncome);
      }
    });
  });

  doc.querySelectorAll('tr.pal-code-account-group-total-row').forEach((tr) => {
    const groupName = extractGroupNameFromTotalRow(tr);
    const groupSum = sumAmountsForAccountGroup(doc, groupName, source);
    const amountTd = tr.querySelector('td.td-last-sum') || tr.querySelector('td.td-last');
    const pctTd = tr.querySelector('td.td-percentage');
    if (amountTd) setAmountCellDisplay(amountTd, groupSum);
    if (pctTd) setPercentageDisplay(pctTd, groupSum, totalIncome);
  });

  let runningTotal = 0;
  const documentTotal = accountRows.reduce((sum, { amount }) => sum + amount, 0);

  doc.querySelectorAll('tr').forEach((tr) => {
    if (isEditableDataRow(tr, source)) {
      runningTotal += getAmountFromCell(getEditableAmountCell(tr, doc, source));
    } else if (tr.classList.contains('tr-final-row-palcode-category')) {
      const label = tr.querySelector('.total-palcode')?.textContent || '';
      if (label.includes('רווח והפסד לאחר')) {
        const amountTd = tr.querySelector('td.td-last-sum') || tr.querySelector('td.td-last');
        const pctTd = tr.querySelector('td.td-percentage');
        if (amountTd) setAmountCellDisplay(amountTd, runningTotal);
        if (pctTd) setPercentageDisplay(pctTd, runningTotal, totalIncome);
      }
    } else if (tr.classList.contains('tr-final-row-palcode-Report')) {
      const amountTds = tr.querySelectorAll('td.td-last-sum-1');
      if (amountTds[0]) setAmountCellDisplay(amountTds[0], documentTotal);
      if (amountTds[1]) setPercentageDisplay(amountTds[1], documentTotal, totalIncome);
    }
  });

  if (source === EDITABLE_SOURCE_PL_REPORT) {
    recalculatePlProfitIndicatorRows(doc, source);
  }

  syncPartnersEquityWithDocument(doc, source);
};

const notifyEditCommitted = (cell, value, onEditCommitted, source = EDITABLE_SOURCE_LEDGER) => {
  if (typeof onEditCommitted !== 'function') return;
  const row = cell?.closest('tr.hoverTr') || cell?.closest('tr.tr');
  const key = row ? makeEditKeyFromCell(row, cell, source) : null;
  if (!key) return;
  if (value == null) {
    onEditCommitted(key, null);
    return;
  }
  onEditCommitted(key, value);
};

const notifyRenameCommitted = (row, value, onRenameCommitted, source = EDITABLE_SOURCE_LEDGER) => {
  if (typeof onRenameCommitted !== 'function' || !row) return;
  const rowKey = row.getAttribute('data-original-row-key') || makeEditKeyFromRow(row, source);
  if (!rowKey) return;
  if (value == null) {
    onRenameCommitted(rowKey, null);
    return;
  }
  onRenameCommitted(rowKey, value);
};

const getStableRowKey = (row, source = EDITABLE_SOURCE_LEDGER) =>
  row?.getAttribute('data-original-row-key') || makeEditKeyFromRow(row, source);

export const makeEditKeyFromCell = (row, cell, source = EDITABLE_SOURCE_LEDGER) => {
  const baseKey = makeEditKeyFromRow(row, source);
  if (!baseKey) return null;
  if (source === EDITABLE_SOURCE_PL_REPORT) {
    const periodIndex = cell?.getAttribute('data-period-index');
    if (periodIndex != null) return `${baseKey}|period:${periodIndex}`;
  }
  return baseKey;
};

export const makeEditKeyFromRow = (row, source = EDITABLE_SOURCE_LEDGER) => {
  if (!row) return null;

  if (row.getAttribute('data-added-row') === 'true') {
    const localRowId = row.getAttribute('data-local-row-id');
    return localRowId ? `added:${localRowId}` : null;
  }

  const sortCodeName = row.getAttribute('data-sort-code-name')
    || getAccountCellLabel(row.querySelector('.td-account-cell'))
    || '';
  const group = row.getAttribute('data-account-group') || getAccountGroupFromRow(row);

  if (source === EDITABLE_SOURCE_PL_REPORT || sortCodeName) {
    if (sortCodeName && group) return `sortcode:${group}|${sortCodeName}`;
    if (sortCodeName) return `sortcode:${sortCodeName}`;
  }

  const palCode = row.getAttribute('data-pal-code') || getPalCodeFromRow(row);
  const accountCardId = row.getAttribute('data-account-card-id');
  const accountCardName = getAccountCellLabel(row.querySelector('.td-account-cell'));
  if (accountCardId && palCode) return `id:${accountCardId}|${palCode}`;
  if (accountCardName && palCode) return `name:${accountCardName}|${palCode}`;
  return null;
};

export const commitInlineEdit = (cell, doc, { onEditCommitted, source = EDITABLE_SOURCE_LEDGER } = {}) => {
  const input = cell?.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (!input || !doc) return false;

  const parsed = parseEditableAmount(input.value);
  if (isNaN(parsed)) {
    return false;
  }

  const wasEdited = cell.classList.contains(EDITED_CLASS);
  const inlineOriginal = cell.getAttribute('data-inline-original') || '';
  const beforeParsed = parseEditableAmount(inlineOriginal);
  const unchanged = !isNaN(beforeParsed) && parsed === beforeParsed;

  cell.innerHTML = '';
  setAmountCellDisplay(cell, parsed);
  cell.classList.remove(SELECTED_CLASS);
  cell.removeAttribute('data-inline-original');

  if (unchanged) {
    if (wasEdited) {
      cell.classList.add(EDITED_CLASS);
    } else {
      cell.classList.remove(EDITED_CLASS);
    }
    return true;
  }

  const baselineParsed = parseEditableAmount(cell.getAttribute('data-original-text') || '');
  if (!isNaN(baselineParsed) && parsed === baselineParsed) {
    cell.classList.remove(EDITED_CLASS);
    notifyEditCommitted(cell, null, onEditCommitted, source);
  } else {
    cell.classList.add(EDITED_CLASS);
    notifyEditCommitted(cell, parsed, onEditCommitted, source);
  }

  recalculateReportTotals(doc, source);
  return true;
};

export const applyLocalEditsToDocument = (doc, editsMap, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc || !editsMap?.size) return;

  getEditableRows(doc, source).forEach((row) => {
    getEditableAmountCells(row, doc, source).forEach((cell) => {
      const key = makeEditKeyFromCell(row, cell, source);
      if (!key || !editsMap.has(key)) return;
      const value = editsMap.get(key);
      cell.innerHTML = '';
      setAmountCellDisplay(cell, value);
      cell.classList.add(EDITED_CLASS);
    });
  });

  recalculateReportTotals(doc, source);
};

export const applyAllLocalEditsToDocument = (
  doc,
  {
    amountEdits = new Map(),
    renames = new Map(),
  } = {},
  source = EDITABLE_SOURCE_LEDGER,
) => {
  if (!doc) return;

  applyRenamesToDocument(doc, renames, source);
  applyLocalEditsToDocument(doc, amountEdits, source);
};

export const flushPendingInlineEdit = (doc, options = {}) => {
  const { source = EDITABLE_SOURCE_LEDGER, ...rest } = options;
  const input = doc?.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (!input) return;
  const amountCell = input.closest('td.td-amount-cell');
  if (amountCell) {
    commitInlineEdit(amountCell, doc, { ...rest, source });
    return;
  }
  const accountCell = input.closest('td.td-account-cell');
  if (accountCell) {
    commitInlineAccountNameEdit(accountCell, doc, { ...rest, source });
  }
};

export const commitInlineAccountNameEdit = (
  cell,
  doc,
  { onRenameCommitted, source = EDITABLE_SOURCE_LEDGER } = {},
) => {
  const input = cell?.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (!input || !doc) return false;

  const nextName = String(input.value || '').trim();
  if (!nextName) return false;

  const wasEdited = cell.classList.contains(EDITED_CLASS);
  const inlineOriginal = cell.getAttribute('data-inline-original') || '';
  const unchanged = nextName === inlineOriginal.trim();

  const row = cell.closest('tr.hoverTr') || cell.closest('tr.tr');
  input.remove();
  setAccountCellLabel(cell, nextName);
  cell.classList.remove(SELECTED_CLASS);
  cell.removeAttribute('data-inline-original');

  if (unchanged) {
    if (wasEdited) {
      cell.classList.add(EDITED_CLASS);
    } else {
      cell.classList.remove(EDITED_CLASS);
    }
    return true;
  }

  const baselineName = cell.getAttribute('data-original-account-name') || '';
  if (nextName === baselineName) {
    cell.classList.remove(EDITED_CLASS);
    notifyRenameCommitted(row, null, onRenameCommitted, source);
  } else {
    cell.classList.add(EDITED_CLASS);
    notifyRenameCommitted(row, nextName, onRenameCommitted, source);
  }
  return true;
};

export const openAccountNameEditor = (
  cell,
  doc,
  direction,
  { onEditCommitted, onRenameCommitted, source = EDITABLE_SOURCE_LEDGER } = {},
) => {
  if (!cell || !doc || cell.querySelector(`.${INLINE_INPUT_CLASS}`)) return;

  const existingEditor = doc.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (existingEditor) {
    const parentAmountCell = existingEditor.closest('td.td-amount-cell');
    const parentAccountCell = existingEditor.closest('td.td-account-cell');
    if (parentAmountCell) {
      commitInlineEdit(parentAmountCell, doc, { onEditCommitted, source });
    } else if (parentAccountCell && parentAccountCell !== cell) {
      commitInlineAccountNameEdit(parentAccountCell, doc, { onRenameCommitted, source });
    }
  }

  doc.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));

  ensureAccountCellStructure(cell);
  const originalText = getAccountCellLabel(cell);
  cell.setAttribute('data-inline-original', originalText);

  const inner = cell.querySelector(`.${ACCOUNT_CELL_INNER_CLASS}`);
  if (inner) inner.style.display = 'none';

  const input = doc.createElement('input');
  input.type = 'text';
  input.className = INLINE_INPUT_CLASS;
  input.value = originalText.replace(/\s+/g, ' ').trim();
  input.setAttribute('dir', direction);
  input.style.textAlign = direction === 'rtl' ? 'right' : 'left';

  cell.appendChild(input);
  input.focus();
  input.select();

  let cancelled = false;

  const restoreCell = (text) => {
    input.remove();
    if (inner) inner.style.display = '';
    setAccountCellLabel(cell, text);
    cell.classList.remove(SELECTED_CLASS);
    cell.removeAttribute('data-inline-original');
  };

  const cancelEdit = () => {
    cancelled = true;
    restoreCell(cell.getAttribute('data-inline-original') || originalText);
  };

  const saveEdit = () => {
    if (cancelled) return;
    const ok = commitInlineAccountNameEdit(cell, doc, { onRenameCommitted, source });
    if (!ok) cancelEdit();
    else if (inner) inner.style.display = '';
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  });

  input.addEventListener('blur', () => {
    window.setTimeout(() => {
      if (!cancelled && cell.contains(input)) {
        saveEdit();
      }
    }, 0);
  });
};

export const openInlineEditor = (cell, doc, direction, { onInvalid, onEditCommitted, onRenameCommitted, source = EDITABLE_SOURCE_LEDGER } = {}) => {
  if (!cell || !doc || cell.querySelector(`.${INLINE_INPUT_CLASS}`)) return;

  const existingEditor = doc.querySelector(`.${INLINE_INPUT_CLASS}`);
  if (existingEditor) {
    const parentCell = existingEditor.closest('td.td-amount-cell');
    const parentAccountCell = existingEditor.closest('td.td-account-cell');
    if (parentCell && parentCell !== cell) {
      commitInlineEdit(parentCell, doc, { onEditCommitted, source });
    } else if (parentAccountCell) {
      commitInlineAccountNameEdit(parentAccountCell, doc, { onRenameCommitted, source });
    }
  }

  doc.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));

  const originalText = (cell.querySelector('.accounting-amount-value')?.textContent ?? cell.textContent).trim();
  cell.setAttribute('data-inline-original', originalText);

  const input = doc.createElement('input');
  input.type = 'text';
  input.className = INLINE_INPUT_CLASS;
  input.value = originalText.replace(/\s+/g, ' ').trim();
  input.setAttribute('dir', direction);
  input.style.textAlign = direction === 'rtl' ? 'right' : 'left';

  cell.innerHTML = '';
  cell.appendChild(input);
  input.focus();
  input.select();

  let cancelled = false;

  const cancelEdit = () => {
    cancelled = true;
    const orig = cell.getAttribute('data-inline-original') || originalText;
    cell.innerHTML = '';
    cell.textContent = orig;
    cell.classList.remove(SELECTED_CLASS);
    cell.removeAttribute('data-inline-original');
  };

  const saveEdit = () => {
    if (cancelled) return;
    const ok = commitInlineEdit(cell, doc, { onEditCommitted, source });
    if (!ok) {
      onInvalid?.();
      cancelEdit();
    }
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  });

  input.addEventListener('blur', () => {
    window.setTimeout(() => {
      if (!cancelled && cell.contains(input)) {
        saveEdit();
      }
    }, 0);
  });
};

export const setupEditableCells = (doc, {
  getMultiSelectMode,
  getSelectedIds,
  onSelectionChange,
  direction = 'rtl',
  onInvalid,
  onEditCommitted,
  onRenameCommitted,
  onRowDeleted,
  onRowAdded,
  onRowAddedRemoved,
  getNextAddedRowId,
  deletedRows = new Set(),
  addedRows = [],
  source = EDITABLE_SOURCE_LEDGER,
  addRowLabel = EMPTY_SECTION_DEFAULT_ADD_LABEL,
}) => {
  if (!doc) return () => {};

  annotateEditableRows(doc, source);
  const listeners = [];
  let cellIndex = 0;

  const rowActionHandlers = {
    onAddRow: (referenceRow) => {
      flushPendingInlineEdit(doc, { onEditCommitted, onRenameCommitted, source });
      const nextId = typeof getNextAddedRowId === 'function' ? getNextAddedRowId() : Date.now();
      const localRowId = `added-row-${nextId}`;
      const descriptor = {
        localRowId,
        afterRowKey: getStableRowKey(referenceRow, source),
        afterLocalRowId:
          referenceRow.getAttribute('data-added-row') === 'true'
            ? referenceRow.getAttribute('data-local-row-id')
            : null,
        accountGroup: referenceRow.getAttribute('data-account-group') || '',
        palCode: referenceRow.getAttribute('data-pal-code') || '',
        sortCodeName: referenceRow.getAttribute('data-sort-code-name') || '',
        accountName: ADDED_ROW_DEFAULT_NAME,
        amounts: {},
      };

      getEditableAmountCells(referenceRow, doc, source).forEach((_cell, index) => {
        descriptor.amounts[index] = 0;
      });

      const newRow = buildAddedRowElement(doc, referenceRow, descriptor, source);
      referenceRow.insertAdjacentElement('afterend', newRow);
      wireEditableRow(newRow);
      onRowAdded?.(descriptor);
      recalculateReportTotals(doc, source);
    },
    onDeleteRow: (row) => {
      flushPendingInlineEdit(doc, { onEditCommitted, onRenameCommitted, source });
      if (row.getAttribute('data-added-row') === 'true') {
        const localRowId = row.getAttribute('data-local-row-id');
        if (localRowId) onRowAddedRemoved?.(localRowId);
      } else {
        const rowKey = getStableRowKey(row, source);
        if (rowKey) onRowDeleted?.(rowKey);
      }
      row.remove();
      refreshEmptySectionControls();
      recalculateReportTotals(doc, source);
    },
  };

  const syncSelectionClasses = (selectedIds) => {
    doc.querySelectorAll('[data-editable-cell]').forEach((cell) => {
      if (cell.querySelector(`.${INLINE_INPUT_CLASS}`)) return;
      const id = cell.getAttribute('data-cell-id');
      if (selectedIds.has(id)) {
        cell.classList.add(SELECTED_CLASS);
      } else {
        cell.classList.remove(SELECTED_CLASS);
      }
    });
  };

  const wireEditableRow = (row) => {
    decorateEditableRow(row, doc, rowActionHandlers);

    const onclick = row.getAttribute('onclick');
    if (onclick) {
      const detailMatch = onclick.match(/detail:\s*'([^']+)'/);
      if (detailMatch?.[1]) {
        try {
          const transaction = JSON.parse(decodeURIComponent(detailMatch[1]));
          if (transaction.accountCardId != null) {
            row.setAttribute('data-account-card-id', String(transaction.accountCardId));
          }
        } catch {
          // ignore malformed row payload
        }
      }
    }
    row.removeAttribute('onclick');

    const stableRowKey = getStableRowKey(row, source);
    if (stableRowKey && !row.getAttribute('data-original-row-key')) {
      row.setAttribute('data-original-row-key', stableRowKey);
    }

    const accountCell = row.querySelector('td.td-account-cell');
    if (accountCell) {
      if (!accountCell.hasAttribute('data-original-account-name')) {
        accountCell.setAttribute('data-original-account-name', getAccountCellLabel(accountCell));
      }

      const onAccountClick = (e) => {
        if (e.target.closest(`.${ROW_ACTIONS_CLASS}`)) return;
        e.stopPropagation();
        openAccountNameEditor(accountCell, doc, direction, {
          onEditCommitted,
          onRenameCommitted,
          source,
        });
      };

      accountCell.addEventListener('click', onAccountClick);
      listeners.push({ cell: accountCell, onClick: onAccountClick });
    }

    getEditableAmountCells(row, doc, source).forEach((cell, periodIndex) => {
      const cellId = `editable-cell-${cellIndex}`;
      cellIndex += 1;

      cell.setAttribute('data-editable-cell', 'true');
      cell.setAttribute('data-cell-id', cellId);
      cell.setAttribute('data-period-index', String(periodIndex));
      if (!cell.hasAttribute('data-original-text')) {
        cell.setAttribute('data-original-text', (cell.textContent || '').trim());
      }

      const onClick = (e) => {
        e.stopPropagation();
        const multiSelectMode = typeof getMultiSelectMode === 'function' ? getMultiSelectMode() : false;

        if (multiSelectMode) {
          const id = cell.getAttribute('data-cell-id');
          const next = new Set(typeof getSelectedIds === 'function' ? getSelectedIds() : []);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          syncSelectionClasses(next);
          onSelectionChange?.(next);
          return;
        }

        openInlineEditor(cell, doc, direction, {
          onInvalid,
          onEditCommitted,
          onRenameCommitted,
          source,
        });
      };

      cell.addEventListener('click', onClick);
      listeners.push({ cell, onClick });
    });
  };

  const addRowToEmptySection = (table) => {
    flushPendingInlineEdit(doc, { onEditCommitted, onRenameCommitted, source });
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const nextId = typeof getNextAddedRowId === 'function' ? getNextAddedRowId() : Date.now();
    const localRowId = `added-row-${nextId}`;
    const referenceRow = buildSyntheticReferenceRow(table, doc);
    const descriptor = {
      localRowId,
      afterRowKey: null,
      afterLocalRowId: null,
      emptySectionKey: getSectionTableKey(doc, table),
      accountGroup: getAccountGroupForTable(table),
      palCode: getPalCodeForTable(table),
      sortCodeName: '',
      accountName: ADDED_ROW_DEFAULT_NAME,
      amounts: {},
    };

    getEditableAmountCells(referenceRow, doc, source).forEach((_cell, index) => {
      descriptor.amounts[index] = 0;
    });

    const newRow = buildAddedRowElement(doc, referenceRow, descriptor, source);
    insertDataRowIntoSection(tbody, newRow);
    wireEditableRow(newRow);
    onRowAdded?.(descriptor);
    refreshEmptySectionControls();
    recalculateReportTotals(doc, source);
  };

  const buildEmptySectionAddRow = (table) => {
    const tr = doc.createElement('tr');
    tr.className = EMPTY_SECTION_ADD_ROW_CLASS;
    tr.setAttribute('data-empty-add-row', 'true');

    const td = doc.createElement('td');
    td.className = EMPTY_SECTION_ADD_CELL_CLASS;
    td.setAttribute('colspan', String(getSectionColumnCount(table)));

    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = EMPTY_SECTION_ADD_BTN_CLASS;
    btn.textContent = `+ ${addRowLabel}`;
    const onClick = (e) => {
      e.stopPropagation();
      addRowToEmptySection(table);
    };
    btn.addEventListener('click', onClick);
    listeners.push({ cell: btn, onClick });

    td.appendChild(btn);
    tr.appendChild(td);
    return tr;
  };

  function refreshEmptySectionControls() {
    doc.querySelectorAll(`.${EMPTY_SECTION_ADD_ROW_CLASS}`).forEach((el) => el.remove());

    getSectionTables(doc).forEach((table) => {
      if (tableHasEditableDataRow(table, source)) return;
      const tbody = table.querySelector('tbody');
      if (!tbody) return;
      insertDataRowIntoSection(tbody, buildEmptySectionAddRow(table));
    });
  }

  applyRowStructureEdits(doc, { deletedRows, addedRows }, source, rowActionHandlers);

  getEditableRows(doc, source).forEach((row) => {
    wireEditableRow(row);
  });

  refreshEmptySectionControls();

  return () => {
    listeners.forEach(({ cell, onClick }) => cell.removeEventListener('click', onClick));
  };
};

export const applyValueToSelectedCells = (doc, selectedCellIds, numericValue, { onEditCommitted, source = EDITABLE_SOURCE_LEDGER } = {}) => {
  if (!doc || !selectedCellIds?.size) return;
  selectedCellIds.forEach((id) => {
    const cell = doc.querySelector(`[data-cell-id="${id}"]`);
    if (cell) {
      cell.innerHTML = '';
      setAmountCellDisplay(cell, numericValue);
      cell.classList.add(EDITED_CLASS);
      cell.classList.remove(SELECTED_CLASS);
      notifyEditCommitted(cell, numericValue, onEditCommitted, source);
    }
  });
  recalculateReportTotals(doc, source);
};

export const getSelectedCellsDisplayTexts = (doc, selectedCellIds) => {
  if (!doc || !selectedCellIds?.size) return [];
  return [...selectedCellIds].map((id) => {
    const cell = doc.querySelector(`[data-cell-id="${id}"]`);
    return (cell?.textContent || '').trim();
  });
};

export const getEditValueForSelection = (doc, selectedCellIds) => {
  const texts = getSelectedCellsDisplayTexts(doc, selectedCellIds);
  if (!texts.length) return '';
  const unique = [...new Set(texts)];
  return unique.length === 1 ? unique[0] : '';
};

export const clearSelectionInDocument = (doc) => {
  if (!doc) return;
  doc.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => {
    if (!el.querySelector(`.${INLINE_INPUT_CLASS}`)) {
      el.classList.remove(SELECTED_CLASS);
    }
  });
};

export const resetEditedCellsInDocument = (doc, source = EDITABLE_SOURCE_LEDGER) => {
  if (!doc) return;
  doc.querySelectorAll('[data-editable-cell]').forEach((cell) => {
    const original = cell.getAttribute('data-original-text');
    cell.innerHTML = '';
    if (original !== null) {
      cell.textContent = original;
    }
    cell.classList.remove(EDITED_CLASS);
    cell.classList.remove(SELECTED_CLASS);
    cell.removeAttribute('data-inline-original');
  });
  doc.querySelectorAll('[data-editable-account-name]').forEach((cell) => {
    const original = cell.getAttribute('data-original-account-name');
    cell.innerHTML = '';
    if (original !== null) {
      cell.textContent = original;
    }
    cell.classList.remove(EDITED_CLASS);
    cell.classList.remove(SELECTED_CLASS);
    cell.removeAttribute('data-inline-original');
  });
  doc.querySelectorAll('[data-added-row="true"]').forEach((row) => {
    row.remove();
  });
  recalculateReportTotals(doc, source);
};

/** Map edited balances from iframe account rows (current displayed values). */
export const getEditedBalancesFromDocument = (doc) => {
  const byId = new Map();
  const byName = new Map();

  if (!doc) return { byId, byName };

  doc.querySelectorAll('tr.hoverTr').forEach((row) => {
    const palCode = row.getAttribute('data-pal-code') || '';
    const accountCardId = row.getAttribute('data-account-card-id');
    const accountCardName = row.querySelector('.td-account-cell')?.textContent?.trim() || '';
    const amount = getAmountFromCell(row.querySelector('td.td-amount-cell'));

    if (accountCardId && palCode) {
      byId.set(`${accountCardId}|${palCode}`, amount);
    }
    if (accountCardName && palCode) {
      byName.set(`${accountCardName}|${palCode}`, amount);
    }
  });

  return { byId, byName };
};

/**
 * Export P&L report layout to Excel from the editable iframe DOM (WYSIWYG).
 */
export const downloadEditableProfitAndLossExcel = ({
  doc,
  reportData,
  t,
  showPercentageColumn = false,
  columnMode,
}) => {
  if (!doc) {
    return { ok: false, reason: 'noDocument' };
  }

  return buildProfitAndLossExcelFromDocument(doc, {
    reportData,
    showPercentageColumn,
    columnMode,
    t,
  });
};
